import { http } from '../interceptor';

/**
 * Upload file to S3 using presigned URL
 * @param {File} file - The file to upload
 * @returns {Promise<string>} - The S3 URL of uploaded file
 */
export const uploadFileToS3 = async (file) => {
  try {
    // Sanitize filename to remove spaces and special characters
    const sanitizeFileName = (fileName) => {
      // Guard: if fileName is undefined/null, use a default
      if (!fileName || typeof fileName !== 'string') {
        return `upload_${Date.now()}.jpg`;
      }

      // Remove file extension
      const lastDotIndex = fileName.lastIndexOf('.');
      const name = fileName.substring(0, lastDotIndex) || 'upload';
      const extension = fileName.substring(lastDotIndex) || '.jpg';
      
      // Add timestamp for uniqueness
      const timestamp = Date.now();
      
      // Remove spaces, parentheses, and special characters
      const sanitizedName = name
        .replace(/\s+/g, '_')                    // Replace spaces with underscores
        .replace(/[()]/g, '')                   // Remove parentheses
        .replace(/[^a-zA-Z0-9_]/g, '')        // Remove special characters except underscores
        .replace(/_{2,}/g, '_')                // Replace multiple underscores with single
        .replace(/^_|_$/g, '');                 // Remove leading/trailing underscores
      
      return `${sanitizedName || 'upload'}_${timestamp}${extension}`;
    };

    // Guard: ensure file is a proper File/Blob object
    if (!file || (typeof file === 'string')) {
      throw new Error('Invalid file: expected a File or Blob object, got ' + typeof file);
    }

    const sanitizedFileName = sanitizeFileName(file.name);
    console.log('Original filename:', file.name);
    console.log('Sanitized filename:', sanitizedFileName);
    
    // Get presigned URL from backend with authentication
    const response = await http.get('/common/s3/presigned-url/', {
      params: {
        file_name: sanitizedFileName,
        content_type: file.type
      }
    });

    const responseData = response.data;
    
    // Debug logging to verify response structure
    console.log('=== S3 Upload Debug ===');
    console.log('Original filename:', file.name);
    console.log('Sanitized filename:', sanitizedFileName);
    console.log('File type:', file.type);
    console.log('File size:', file.size);
    console.log('Is PNG file:', file.type === 'image/png');
    console.log('Response data:', responseData);
    
    // Handle both response structures (direct and wrapped)
    let url, key;
    if (responseData.data) {
      // Response is wrapped in data object
      url = responseData.data.url;
      key = responseData.data.key;
    } else {
      // Direct response structure
      url = responseData.url;
      key = responseData.key;
    }
    
    // Safety check - ensure we have required values
    if (!url || !key) {
      console.error('Missing url or key in response:', { url, key, responseData });
      throw new Error('Invalid response from presigned URL endpoint: missing url or key');
    }

    // Upload file directly to S3 using presigned URL
    const uploadResponse = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type
      },
      body: file
    });
    
    if (!uploadResponse.ok) {
      console.error('S3 Upload Failed:', {
        status: uploadResponse.status,
        statusText: uploadResponse.statusText,
        url: url
      });
      throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
    }
    
    // Return S3 file URL
    const s3Url = `https://mastermart-prod-bucket.s3.ap-south-1.amazonaws.com/${key}`;
    
    console.log('Extracted URL:', url);
    console.log('Extracted Key:', key);
    console.log('Final S3 URL:', s3Url);
    
    return s3Url;
  } catch (error) {
    console.error('S3 Upload Error Details:', error);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
    throw new Error(`Failed to upload file to S3: ${error.message}`);
  }
};
